import type { QueryClient } from '@tanstack/react-query';

import type { CompanySearchApiResult } from '../../../../../shared/api/fetchCompanySearchResults';
import {
  companyQuery,
  directSubsidiariesQuery,
  isMissingMapperRoute,
  subsidiariesOfCompaniesQuery,
} from './company-mapper-queries';
import {
  expandSubsidiaries,
  type SubsidiaryRecord,
  type SubsidiaryTreeNode,
} from './expand-subsidiaries';

const SUBSIDIARY_LEVELS = 4;
const MAX_PARENTS_PER_LEVEL = 25;
const MAX_CHILDREN_PER_PARENT = 100;
const MAX_NODES = 1500;
const FALLBACK_CONCURRENCY = 6;

const toRecord = (company: CompanySearchApiResult): SubsidiaryRecord => ({
  rcid: company.rcid,
  name: company.primary_name,
  total: company.raw_emp_count,
  hasSubsidiaries: company.has_subsidiaries,
});

export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  concurrency: number,
  mapper: (item: T) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let cursor = 0;
  const drain = async () => {
    while (cursor < items.length) {
      const index = cursor++;
      results[index] = await mapper(items[index] as T);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(concurrency, items.length) }, drain)
  );
  return results;
}

/**
 * The root company and its own subsidiaries come from the page's shared
 * company-mapper queries, so the other cards reuse them; every deeper level is
 * one `/get-companies` batch. Mappers that predate that endpoint answer 404,
 * and the rest of the tree then falls back to one `/subsidiaries` per parent.
 */
export const fetchSubsidiaryTree = async (
  rootRcid: number,
  queryClient: QueryClient
): Promise<SubsidiaryTreeNode> => {
  const fetchOneByOne = async (rcids: number[]) => {
    const lists = await mapWithConcurrency(
      rcids,
      FALLBACK_CONCURRENCY,
      (rcid) =>
        queryClient
          .fetchQuery(directSubsidiariesQuery(rcid))
          .catch((): CompanySearchApiResult[] => [])
    );
    return new Map(rcids.map((rcid, index) => [rcid, lists[index] ?? []]));
  };

  let canBatch = true;
  const fetchLevelChildren = async (rcids: number[]) => {
    if (canBatch) {
      try {
        return await queryClient.fetchQuery(
          subsidiariesOfCompaniesQuery(rcids)
        );
      } catch (error) {
        if (isMissingMapperRoute(error)) canBatch = false;
      }
    }
    return fetchOneByOne(rcids);
  };

  const [company, rootChildren] = await Promise.all([
    queryClient.fetchQuery(companyQuery(rootRcid)).catch(() => null),
    queryClient.fetchQuery(directSubsidiariesQuery(rootRcid)),
  ]);
  const childrenTotal = rootChildren.reduce(
    (total, child) => total + (child.raw_emp_count ?? 0),
    0
  );
  return expandSubsidiaries({
    root: {
      rcid: rootRcid,
      name: company?.primary_name ?? '',
      total: Math.max(company?.raw_emp_count ?? 0, childrenTotal),
      hasSubsidiaries: rootChildren.length > 0,
    },
    depth: SUBSIDIARY_LEVELS,
    fetchLevel: async (parentRcids) => {
      const byParent =
        parentRcids.length === 1 && parentRcids[0] === rootRcid
          ? new Map([[rootRcid, rootChildren]])
          : await fetchLevelChildren(parentRcids);
      return new Map(
        [...byParent].map(([rcid, children]) => [rcid, children.map(toRecord)])
      );
    },
    maxParentsPerLevel: MAX_PARENTS_PER_LEVEL,
    maxChildrenPerParent: MAX_CHILDREN_PER_PARENT,
    maxNodes: MAX_NODES,
  });
};
